import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  TextField,
  Grid
} from '@mui/material';
import {
  CardElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import { stripeService } from '../services/stripe/StripeService';
import { EscrowPaymentData } from '../services/stripe/config';

interface StripePaymentFormProps {
  paymentData: EscrowPaymentData;
  onPaymentSuccess: (paymentIntent: any) => void;
  onPaymentError: (error: string) => void;
}

const StripePaymentForm: React.FC<StripePaymentFormProps> = ({
  paymentData,
  onPaymentSuccess,
  onPaymentError
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [cardError, setCardError] = useState<string | null>(null);
  const [billingDetails, setBillingDetails] = useState({
    name: '',
    email: '',
    address: {
      line1: '',
      city: '',
      postal_code: '',
      country: 'FR'
    }
  });

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements) {
      onPaymentError('Stripe n\'est pas encore chargé');
      return;
    }

    setIsProcessing(true);
    setCardError(null);

    try {
      // Créer le Payment Intent
      const paymentIntent = await stripeService.createEscrowPaymentIntent(paymentData);
      
      // Obtenir l'élément carte
      const cardElement = elements.getElement(CardElement);
      if (!cardElement) {
        throw new Error('Élément carte non trouvé');
      }

      // Confirmer le paiement
      const { error, paymentIntent: confirmedPayment } = await stripe.confirmCardPayment(
        paymentIntent.client_secret,
        {
          payment_method: {
            card: cardElement,
            billing_details: billingDetails,
          },
        }
      );

      if (error) {
        setCardError(error.message || 'Erreur de paiement');
        onPaymentError(error.message || 'Erreur de paiement');
      } else if (confirmedPayment && confirmedPayment.status === 'succeeded') {
        onPaymentSuccess(confirmedPayment);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erreur inconnue';
      setCardError(errorMessage);
      onPaymentError(errorMessage);
    } finally {
      setIsProcessing(false);
    }
  };

  const cardElementOptions = {
    style: {
      base: {
        fontSize: '16px',
        color: '#424770',
        '::placeholder': {
          color: '#aab7c4',
        },
        fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      },
      invalid: {
        color: '#9e2146',
      },
    },
    hidePostalCode: false,
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Typography variant="h6" gutterBottom>
        Informations de facturation
      </Typography>
      
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Nom complet"
            value={billingDetails.name}
            onChange={(e) => setBillingDetails({
              ...billingDetails,
              name: e.target.value
            })}
            required
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Email"
            type="email"
            value={billingDetails.email}
            onChange={(e) => setBillingDetails({
              ...billingDetails,
              email: e.target.value
            })}
            required
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Adresse"
            value={billingDetails.address.line1}
            onChange={(e) => setBillingDetails({
              ...billingDetails,
              address: {
                ...billingDetails.address,
                line1: e.target.value
              }
            })}
            required
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Ville"
            value={billingDetails.address.city}
            onChange={(e) => setBillingDetails({
              ...billingDetails,
              address: {
                ...billingDetails.address,
                city: e.target.value
              }
            })}
            required
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Code postal"
            value={billingDetails.address.postal_code}
            onChange={(e) => setBillingDetails({
              ...billingDetails,
              address: {
                ...billingDetails.address,
                postal_code: e.target.value
              }
            })}
            required
          />
        </Grid>
      </Grid>

      <Typography variant="h6" gutterBottom>
        Informations de carte
      </Typography>
      
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <CardElement options={cardElementOptions} />
        </CardContent>
      </Card>

      {cardError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {cardError}
        </Alert>
      )}

      <Alert severity="info" sx={{ mb: 2 }}>
        <Typography variant="body2">
          <strong>Sécurité :</strong> Vos informations de carte sont sécurisées par Stripe. 
          Le montant total ({paymentData.totalAmount}€) sera bloqué jusqu'à confirmation mutuelle des deux parties.
        </Typography>
      </Alert>

      <Button
        type="submit"
        variant="contained"
        fullWidth
        disabled={!stripe || isProcessing}
        sx={{ 
          backgroundColor: '#a5816d', 
          '&:hover': { backgroundColor: '#8B7355' },
          py: 1.5
        }}
      >
        {isProcessing ? (
          <>
            <CircularProgress size={20} sx={{ mr: 1 }} />
            Traitement du paiement...
          </>
        ) : (
          `Payer ${paymentData.totalAmount}€ et bloquer les fonds`
        )}
      </Button>
    </Box>
  );
};

export default StripePaymentForm;
